import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, Trash2, ExternalLink, Filter } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ALERT_SEVERITY_META, ALERT_TYPE_LABELS, alertDeepLink,
} from "@/lib/alerts";

const ALERT_TYPES = [
  "stalled_order",
  "delivery_blocked_by_balance",
  "low_stock_critical",
  "slow_moving_inventory",
  "overdue_payable",
  "purchase_order_no_reception",
];

const SEVERITIES = ["critical", "warning", "info"];

export default function Alertas() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const canAck = hasPermission("alerts:acknowledge");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState(searchParams.get("severity") || "all");
  const [type, setType] = useState(searchParams.get("type") || "all");
  const [status, setStatus] = useState("open");
  const [busyId, setBusyId] = useState(null);

  const tenantParam = useMemo(
    () => (isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {}),
    [isSuperAdmin, activeTenantId],
  );

  async function fetchList() {
    setLoading(true);
    try {
      const { data } = await api.get("/alerts", {
        params: {
          ...tenantParam,
          severity: severity !== "all" ? severity : undefined,
          type: type !== "all" ? type : undefined,
          status: status !== "all" ? status : undefined,
        },
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [severity, type, status, activeTenantId]);

  async function acknowledge(a) {
    setBusyId(a.id);
    try {
      await api.post(`/alerts/${a.id}/acknowledge`);
      toast.success("Alerta reconocida");
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusyId(null); }
  }

  async function dismiss(a) {
    setBusyId(a.id);
    try {
      await api.post(`/alerts/${a.id}/dismiss`);
      toast.success("Alerta descartada");
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusyId(null); }
  }

  const grouped = useMemo(() => {
    const g = { critical: [], warning: [], info: [] };
    for (const a of items) g[a.severity]?.push(a);
    return g;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="page-alerts">
      <PageHeader
        title="Alertas operativas"
        description="Alertas automáticas para detectar situaciones que requieren atención. Cada alerta lleva a la acción útil correspondiente."
      />

      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-3">
            <Filter className="h-3.5 w-3.5" /> Filtros
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid="alerts-filter-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las severidades</SelectItem>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s}>{ALERT_SEVERITY_META[s]?.label || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="alerts-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {ALERT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{ALERT_TYPE_LABELS[t] || t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="alerts-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Abiertas</SelectItem>
                <SelectItem value="acknowledged">Reconocidas</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground" data-testid="alerts-count">
        {items.length} {items.length === 1 ? "alerta" : "alertas"}
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState title="Sin alertas" description="No hay alertas que coincidan con los filtros actuales." icon={AlertTriangle} />
      ) : (
        ["critical", "warning", "info"].map((sev) => {
          const list = grouped[sev];
          if (!list || list.length === 0) return null;
          const meta = ALERT_SEVERITY_META[sev];
          return (
            <div key={sev} className="space-y-2" data-testid={`alerts-group-${sev}`}>
              <div className="flex items-center gap-2">
                <Badge className={meta.badgeClass}>{meta.label}</Badge>
                <span className="text-xs text-muted-foreground">{list.length}</span>
              </div>
              {list.map((a) => (
                <Card key={a.id} className={`border-border ${meta.rowClass}`} data-testid={`alert-row-${a.id}`}>
                  <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{a.title}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {ALERT_TYPE_LABELS[a.type] || a.type}
                        </Badge>
                        {a.status === "acknowledged" && (
                          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">Reconocida</Badge>
                        )}
                        {a.computed && (
                          <Badge variant="outline" className="text-[10px] bg-slate-50">Calculada</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.message}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {a.generated_at?.slice(0, 16).replace("T", " ")}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => navigate(alertDeepLink(a))}
                        data-testid={`alert-goto-${a.id}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ir a
                      </Button>
                      {canAck && !a.computed && a.status === "open" && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => acknowledge(a)}
                          disabled={busyId === a.id}
                          data-testid={`alert-ack-${a.id}`}
                        >
                          <Check className="h-3.5 w-3.5 mr-1" /> Reconocer
                        </Button>
                      )}
                      {canAck && !a.computed && (a.status === "open" || a.status === "acknowledged") && (
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => dismiss(a)}
                          disabled={busyId === a.id}
                          data-testid={`alert-dismiss-${a.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Descartar
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}
